import {
  ChangeDetectorRef, ChangeDetectionStrategy, Component, HostBinding, EventEmitter, Input,
  OnInit, OnDestroy, OnChanges, Output, SimpleChanges, ViewChild, HostListener, ElementRef,
} from '@angular/core';
import * as _ from 'lodash';
import { Subject } from 'rxjs';

import { ActiveFilterModel } from '../active-filters/active-filter.model';
import { FilterSearchModel } from '../vehicle-search/filter-search.model';
import { VEHICLE_FILTER_TYPE, VCDB_FILTER_TYPE, LIST_FILTER_TYPE, SIMPLE_FILTER_TYPE } from '../active-filters/active-filter-types';
import {
  REGION_TYPE,
  VEHICLE_TYPE,
  MAKE_TYPE,
  MODEL_TYPE,
  YEAR_START_TYPE,
  YEAR_END_TYPE,
} from './filter-types';
import { createOption } from '../../form/select/option-helpers';

import { LoaderComponent } from '../../loader/loader.component';

import { BrandService } from '../../brand/brand.service';


@Component({
  providers: [],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseAdvancedSearchComponent implements OnInit, OnChanges, OnDestroy {
  [key: string]: any;

  @HostBinding('class.advanced-search-host')
  hostClass: boolean = true;

  @HostBinding('class.advanced-search-show')
  @Input() show: boolean = false;

  @Input() filters: Array<ActiveFilterModel> = []; // all assigned filters

  @Input() ghostValue: string;

  @Input() itemId: any;

  @Input() hasSpinner: boolean = true;

  @Input() searchResultsColumns: Array<any>;

  @Output() runSearch: EventEmitter<any> = new EventEmitter();

  @Output() closeClicked: EventEmitter<any> = new EventEmitter();

  @ViewChild('searchFiltersEl', {static: false})
  searchFiltersEl: ElementRef | void;

  @ViewChild('scrollEl', {static: false})
  scrollEl: ElementRef | void;

  @ViewChild(LoaderComponent, {static: false})
  loader: LoaderComponent | void;

  vehicleSearch: any;
  vehicleAttributes: any;

  spinnerIsShown: boolean = false;
  spinnerShowCalls: number = 0;
  filterObsData = new Subject<any>();
  attributeObsData = new Subject<any>();
  activeFilters: Array<ActiveFilterModel> = []; // filters related to advanced search (vehicle, vcdb, partAttributes)
  filterPlaceholders: any;
  attributesList: Array<any> = []; // all attributes list
  excludeFilters: Array<string> = [LIST_FILTER_TYPE, SIMPLE_FILTER_TYPE]; // filters not included in advanced search


  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  @HostBinding('class.has-filters')
  get hasFilters(): boolean {
    return _.size(this.activeFilters) > 0;
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  get filterSearchModel(): FilterSearchModel | void {
    if (!this.vehicleSearch) {
        return;
    }
    return this.vehicleSearch.filterSearchModel;
  }

  get vehicleFilters(): Array<ActiveFilterModel> {
    return this.vehicleSearch.combinedFilters;
  }

  get selectedVcdbFilters(): Array<ActiveFilterModel> {
    return this.vehicleAttributes.selectedVcdbFilters;
  }

  get hasActiveAttributeGroup(): boolean {
    return _.get(this, 'vehicleAttributes.hasActiveGroup');
  }

  get searchFiltersElHeight(): number {
    return _.get(this, 'searchFiltersEl.nativeElement.offsetHeight', 0);
  }

  get scrollMaxHeight(): string {
    return `calc(100vh - ${285 + this.searchFiltersElHeight}px)`;
  }

  get hasScrollbar(): boolean {
    if (!this.scrollEl) { return false; }
    return _.get(this.scrollEl, 'nativeElement.scrollHeight') >
           _.get(this.scrollEl, 'nativeElement.clientHeight');
  }

  constructor(
    public brandService: BrandService,
    protected ref: ChangeDetectorRef,
  ) {}

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    this.fetchAttributeGroups();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.show && changes.show.currentValue) {
      this.initAdvancedSearch();
    } else {
      this.resetAdvancedSearch();
    }
  }

  ngOnDestroy() {
    this.activeFilters = [];
    this.filterObsData.complete();
    this.attributeObsData.complete();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  @HostListener('keydown.tab', ['$event'])
  onTabKeydown(event: any): any {
    if (this.spinnerIsShown) {
      event.stopPropagation();
      return false;
    }
  }

  onFilterRemove($event: any) {
    this.removeFilter($event);
  }

  onClearFiltersClick() {
    this.clearFilters();
  }

  onVehicleOptionsFetch(filterName: string) {
    this.showSpinner(true);
    this.fetchOptions(filterName, this.filterSearchModel)
    .subscribe((data: Array<any>) => {
      this.vehicleSearch.setSelectData(data, filterName);
      _.defer(() => this.showSpinner(false));
    });
  }

  onAttributeOptionsFetch(name: string) {
    this.showSpinner(true);
    this.fetchOptions(name, this.filterSearchModel)
    .subscribe((data: Array<any>) => {
      this.vehicleAttributes.setOptions(data, name);
      _.defer(() => this.showSpinner(false));
    });
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public resetVehicleSelectboxes(): void {
    if (!this.vehicleSearch) {
        return;
    }
    this.vehicleSearch.resetSelectboxes();
  }

  public setYearMakeModel(data?: any) {
    // set selected make model year
    this.vehicleSearch.setYearMakeModel(data);
  }

  public getVcdbAttributesFilter(): any {
    return this.vehicleAttributes.getFiltersData();
  }

  public getVcdbFilter(): any {
    return this.vehicleSearch.getFiltersData();
  }

  public resetAllData(): void {
    _.remove(this.activeFilters);
    this.resetVehicleSelectboxes();
    if (!this.vehicleAttributes) {
        return;
    }
    this.vehicleAttributes.resetData();
  }

  public clearFilters(): void {
    _.remove(this.activeFilters);
    this.resetVehicleSelectboxes();
    this.vehicleAttributes.resetSelectedData();
    this.onVehicleOptionsFetch(REGION_TYPE);
    this.onVehicleOptionsFetch(VEHICLE_TYPE);
  }

  public removeFilter(filter: ActiveFilterModel): void {
    if (filter.type === VEHICLE_FILTER_TYPE) {
      this.vehicleSearch.handleFilterRemove(filter);
    } else if (filter.type === VCDB_FILTER_TYPE) {
      this.vehicleAttributes.handleFilterRemove(filter);
    }
    _.remove(this.activeFilters, filter);
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  protected showSpinner(show: boolean): void {
    if (this.loader) {
      if (show) {
        this.spinnerShowCalls++;
        this.spinnerIsShown = show;
        this.loader.show();
      } else {
        this.spinnerShowCalls--;
        if (this.spinnerShowCalls <= 0) {
          this.spinnerIsShown = show;
          this.loader.hide();
        }
      }
    }
    this.ref.markForCheck();
  }

  protected setActiveFilters(): void {
    this.activeFilters = _.filter(this.filters, (filter: ActiveFilterModel) => {
      return !_.includes(this.excludeFilters, filter.type);
    });
  }

  // Called after opening Advanced Search
  // Configure all search components within Advanced Search
  // May be extend after adding new search components
  protected initAdvancedSearch(): void {
    this.vehicleAttributes.resetData();
    // set vehicle, vcdb, partAttr filters
    this.setActiveFilters();
    this.fetchAttributeGroups();
    this.fetchAttributes();
    _.defer(() => {
      this.vehicleSearch.setActiveFiltersData();
      // fetch data for multiple selects:
      this.onVehicleOptionsFetch(REGION_TYPE);
      this.onVehicleOptionsFetch(VEHICLE_TYPE);
    });
  }

  // Called after closing advanced search
  // Reset unnecessary data of all search components within Advanced Search
  // May be extend after adding new search components
  protected resetAdvancedSearch(): void {
    this.resetAllData();
  }

  // set groupd data & add groups data from active filters
  protected setAttributeGroups(data: Array<any>): void {
    // remove Essential tab
    this.attributeGroups = _.reject(data, ['title', 'Essential']);
    _.defer(() => {
      this.vehicleAttributes.setActiveFiltersData();
    });
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/
}
